import {
  dotToCommaTransform,
  formatThousandsFromStringOrNumber,
  removeTrailingCommaOrDot,
} from "@/lib/formatters/NumericTextFieldInputCleaner";
import { ScreenSizes } from "@/lib/utils/ScreenSizesEnum";
import { Indent, PickByValue, Txt } from "@stenajs-webui/core";
import {
  StandardTableCellRenderer,
  StandardTableConfigWithGroups,
  UseGridCellResult,
  createColumnConfig,
  createEditableTextCellWithStatus,
} from "@stenajs-webui/grid";
import { cssColor } from "@stenajs-webui/theme";
import { Dispatch } from "react";
import { formatUnacc } from "../../../../../lib/formatters/AccUnaccFormatter";
import { formatShortCargoStatus } from "../../../../../lib/formatters/CargoStatusFormatter";
import { formatSailingTypeCodesToString } from "../../../../../lib/formatters/SailingStatusCodeFormatter";
import { BulkEditRatesActualChangeInPercent } from "../components/BulkEditRatesActualChangeInPercent";
import { bulkEditRatesStandardTableCell } from "../components/BulkEditRatesStandardTableCell";
import { bulkEditRatesActions } from "../redux/reducer";
import { fetchBulkEditRatesActualChangeForSinglePriceRow } from "../thunks/FetchActualChangeForSinglePriceRow";
import { BulkEditRatePrice, BulkEditRatePriceTableItem } from "../types";
import {
  formatVehicleLength,
  formatWeightFromTo,
  vehicleTypeCell,
} from "../utils/helpers";
import { HeadAgreementStatusCode } from "@/gql/graphql";
import { useSelector } from "react-redux";
import { StoreState } from "@/redux/RootReducer";
import { EditableTableCell } from "@/components/EditableTableCell";
import { inputFieldValidationOnlyNumbersCommasAndDots } from "@/lib/regex/InputFieldValidation";
import { EditableEntityState } from "@stenajs-webui/redux";

const routeAgreementIsEditable = (
  item: BulkEditRatePrice,
  bulkUpdateModeEnabled?: boolean
) => {
  if (bulkUpdateModeEnabled) {
    return false;
  }
  if (
    item?.routeAgreement.statusCode === HeadAgreementStatusCode.Active ||
    item?.routeAgreement.statusCode === HeadAgreementStatusCode.Void
  ) {
    return false;
  }
  return true;
};

export type BulkEditRatesTableColumn =
  | keyof Omit<
      BulkEditRatePrice,
      | "id"
      | "lengthFrom"
      | "lengthTo"
      | "weightFrom"
      | "weightTo"
      | "routeAgreement"
      | "avgLength"
      | "avgRebate"
      | "__typename"
    >
  | "length"
  | "weight"
  | "type"
  | "direction";

interface TableConfigVariables {
  open: () => void;
  isOpen: boolean;
  selectedAgreement: BulkEditRatePrice | undefined;
  setRoutePriceId: (routePriceId: string) => void;
  dispatch: Dispatch<any>;
  bulkUpdateModeEnabled: boolean;
  windowSize: number;
  promptVisible: boolean;
}

const BulkEditRatesTableCell = ({
  id,
  gridCell,
  isEditable,
  propertyName,
}: {
  id: string;
  gridCell: UseGridCellResult<string>;
  isEditable: boolean;
  propertyName: keyof PickByValue<BulkEditRatePriceTableItem, string | null>;
}) => {
  const persistedValue = useSelector((state: StoreState) => {
    return state.bulkEditRatesState.persisted[id]?.[propertyName];
  });
  const editedValue = useSelector((state: StoreState) => {
    return state.bulkEditRatesState.edited[id]?.[propertyName];
  });

  return (
    <div className={propertyName}>
      <EditableTableCell
        gridCell={gridCell}
        isEditable={isEditable}
        onValueChange={(v) => {
          if (inputFieldValidationOnlyNumbersCommasAndDots.test(v)) {
            gridCell.setEditorValue(dotToCommaTransform(v));
          }
        }}
        warningOnEmpty={"Empty is not allowed."}
        includeOldValue
        persistedValue={persistedValue}
        editableValue={editedValue}
      />
    </div>
  );
};

export const createBulkEditRatesTableConfig = ({
  open,
  isOpen,
  selectedAgreement,
  setRoutePriceId,
  dispatch,
  bulkUpdateModeEnabled,
  windowSize,
  promptVisible,
}: TableConfigVariables): StandardTableConfigWithGroups<
  BulkEditRatePrice,
  BulkEditRatesTableColumn
> => {
  return {
    headerRowOffsetTop:
      promptVisible && bulkUpdateModeEnabled
        ? "242px"
        : bulkUpdateModeEnabled
        ? "154px"
        : "98px",
    stickyHeader: true,
    enableGridCell: true,
    showHeaderCheckbox: true,
    showRowCheckbox: true,
    stickyCheckboxColumn:
      windowSize <= ScreenSizes.tabletAndDesktop ? true : false,
    stickyColumnGroups:
      windowSize <= ScreenSizes.tabletAndDesktop ? "first" : undefined,
    checkboxDisabledResolver: (item) => !routeAgreementIsEditable(item),
    keyResolver: (item) => item.id,
    rowBackgroundResolver: (item) =>
      isOpen && selectedAgreement?.id === item.id
        ? cssColor("--lhds-color-blue-50")
        : undefined,

    columns: {
      vehicleType: {
        itemValueResolver: (item) => item.vehicleType?.code,
        columnLabel: "Vehicle type",
        disableGridCell: true,
        width: "107px",
        renderCell: ({ label, item }) =>
          vehicleTypeCell(item, setRoutePriceId, open),
      },
      type: {
        itemValueResolver: (item) =>
          formatSailingTypeCodesToString(item.routeAgreement.sailingTypeCodes),
        itemLabelFormatter: (value, item) =>
          formatSailingTypeCodesToString(item?.routeAgreement.sailingTypeCodes),
        columnLabel: "Type",
        width: "65px",
        disableGridCell: true,
      },
      direction: {
        itemValueResolver: (item) => item.routeAgreement.routeDescription,
        columnLabel: "Direction",
        disableGridCell: true,
        width: "115px",
        renderCell: ({ label, item }) =>
          bulkEditRatesStandardTableCell(item.routeAgreement.routeDescription),
      },
      length: createColumnConfig(
        (item) => formatVehicleLength(item.lengthFrom, item.lengthTo),
        {
          renderCell: ({ value }) => bulkEditRatesStandardTableCell(value),
          disableGridCell: true,
          columnLabel: "Length (m)",
          minWidth: "99px",
        }
      ),
      weight: createColumnConfig(
        (item) => formatWeightFromTo(item.weightFrom, item.weightTo),
        {
          renderCell: ({ value }) => bulkEditRatesStandardTableCell(value),
          disableGridCell: true,
          columnLabel: "Weight (kg)",
          minWidth: "103px",
        }
      ),
      cargoStatusCode: createColumnConfig((item) => item.cargoStatusCode, {
        columnLabel: "ELB",
        disableGridCell: true,
        minWidth: "56px",
        renderCell: ({ value }) =>
          bulkEditRatesStandardTableCell(formatShortCargoStatus(value)),
      }),
      unaccompanied: createColumnConfig(
        (item) => formatUnacc(item.unaccompanied),
        {
          columnLabel: "Unacc",
          minWidth: "56px",
          disableGridCell: true,
          renderCell: ({ value }) => bulkEditRatesStandardTableCell(value),
        }
      ),
      volume: createColumnConfig(
        (item) => formatThousandsFromStringOrNumber(item.volume),
        {
          disableGridCell: true,
          columnLabel: "Volume",
          minWidth: "74px",
          renderCell: ({ value }) => bulkEditRatesStandardTableCell(value),
        }
      ),
      avgGrossPrice: createColumnConfig(
        (item) => dotToCommaTransform(item?.avgGrossPrice?.amount ?? ""),
        {
          disableGridCell: true,
          minWidth: "97px",
          columnLabel: "Avg. gross",
          renderCell: ({ value }) => bulkEditRatesStandardTableCell(value),
        }
      ),
      lastPrice: createColumnConfig(
        (item) => dotToCommaTransform(item.lastPrice?.amount ?? ""),
        {
          disableGridCell: true,
          columnLabel: "Price",
          minWidth: "70px",
          renderCell: ({ value }) => bulkEditRatesStandardTableCell(value),
        }
      ),
      lastMeterPrice: createColumnConfig(
        (item) =>
          dotToCommaTransform(
            formatThousandsFromStringOrNumber(item.lastMeterPrice?.amount)
          ),
        {
          disableGridCell: true,
          minWidth: "84px",
          columnLabel: "M. price",
        }
      ),
      lastChargedFrom: createColumnConfig(
        (item) =>
          dotToCommaTransform(
            formatThousandsFromStringOrNumber(item.lastChargedFrom)
          ),
        {
          disableGridCell: true,
          columnLabel: "Ch. from",
          minWidth: "87px",
        }
      ),
      newPrice: createColumnConfig((item) => item.newPrice.amount, {
        columnLabel: "New price",
        borderLeft: true,
        disableGridCell: bulkUpdateModeEnabled,
        isEditable: (item) =>
          routeAgreementIsEditable(item, bulkUpdateModeEnabled),
        width: "150px",
        renderCell: ({ item, gridCell }) => (
          <BulkEditRatesTableCell
            id={item.id}
            gridCell={gridCell}
            isEditable={routeAgreementIsEditable(item, bulkUpdateModeEnabled)}
            propertyName={"newPrice"}
          />
        ),

        gridCellOptions: {
          allowedInputType: "numeric",
        },
        onChange: (item, value) => {
          dispatch(
            bulkEditRatesActions.setValue({
              id: item.id,
              newPrice: value != null ? removeTrailingCommaOrDot(value) : value,
            })
          );
          dispatch(
            fetchBulkEditRatesActualChangeForSinglePriceRow(
              item.routeAgreement.id,
              item.id
            )
          );
        },
      }),
      meterPrice: createColumnConfig((item) => item.meterPrice.amount, {
        renderCell: ({ label, value, item, gridCell, isEditable }) => (
          <BulkEditRatesTableCell
            id={item.id}
            gridCell={gridCell}
            isEditable={routeAgreementIsEditable(item, bulkUpdateModeEnabled)}
            propertyName={"meterPrice"}
          />
        ),
        gridCellOptions: {
          allowedInputType: "numeric",
        },
        onChange: (item, value) => {
          dispatch(
            bulkEditRatesActions.setValue({
              id: item.id,
              meterPrice:
                value != null ? removeTrailingCommaOrDot(value) : value,
            })
          );
          dispatch(
            fetchBulkEditRatesActualChangeForSinglePriceRow(
              item.routeAgreement.id,
              item.id
            )
          );
        },
        width: "150px",
        columnLabel: "M. price",
        disableGridCell: bulkUpdateModeEnabled,
        isEditable: (item) =>
          routeAgreementIsEditable(item, bulkUpdateModeEnabled),
      }),
      additionalFrom: createColumnConfig((item) => item.additionalFrom, {
        renderCell: ({ item, gridCell }) => (
          <BulkEditRatesTableCell
            id={item.id}
            gridCell={gridCell}
            isEditable={routeAgreementIsEditable(item, bulkUpdateModeEnabled)}
            propertyName={"additionalFrom"}
          />
        ),

        gridCellOptions: {
          allowedInputType: "numeric",
        },
        onChange: (item, value) => {
          dispatch(
            bulkEditRatesActions.setValue({
              id: item.id,
              additionalFrom:
                value != null ? removeTrailingCommaOrDot(value) : value,
            })
          );
          dispatch(
            fetchBulkEditRatesActualChangeForSinglePriceRow(
              item.routeAgreement.id,
              item.id
            )
          );
        },
        disableGridCell: bulkUpdateModeEnabled,
        isEditable: (item) =>
          routeAgreementIsEditable(item, bulkUpdateModeEnabled),
        width: "150px",
        columnLabel: "Add. from",
      }),
      actual: createColumnConfig((item) => item.actual ?? 0, {
        borderLeft: true,
        disableGridCell: true,
        minWidth: "110px",
        columnLabel: "Actual (%)",
        renderCell: ({ label, value, item }) => (
          <Indent>
            <BulkEditRatesActualChangeInPercent entityId={item.id} />
          </Indent>
        ),
      }),
    },
    columnGroups: {
      firstNoLabelColumnGroup: {
        columnOrder: ["vehicleType", "type", "direction"],
      },
      secondNoLabelColumnGroup: {
        columnOrder: ["length", "weight", "cargoStatusCode", "unaccompanied"],
      },
      thirdNoLabelColumnGroup: {
        columnOrder: [
          "volume",
          "avgGrossPrice",
          "lastPrice",
          "lastMeterPrice",
          "lastChargedFrom",
        ],
        borderLeft: true,
      },
      fourthNoLabelColumnGroup: {
        columnOrder: ["newPrice", "meterPrice", "additionalFrom"],
        borderLeft: true,
      },
      fifthNoLabelColumnGroup: {
        columnOrder: ["actual"],
        borderLeft: true,
      },
    },
    columnGroupOrder: [
      "firstNoLabelColumnGroup",
      "secondNoLabelColumnGroup",
      "thirdNoLabelColumnGroup",
      "fourthNoLabelColumnGroup",
      "fifthNoLabelColumnGroup",
    ],
  };
};
